import type { WhatsAppSSEEvent } from '~/types/whatsapp';

export interface Chat {
  uuid: string;
  test: boolean;
  status: string;
  noReply: boolean;
  archived: boolean;
  owner: Interlocutor;
  aiAgentName: string;
  lastMessageDate: string | null;
};

export interface PaginatedChats {
  page: number;
  total: number;
  pageSize: number;
  items: Array<Chat>;
}

export type MappedChat = Omit<Chat, 'lastMessageDate'> & {
  lastMessageDate: Date | null;
};

interface Interlocutor {
  uuid: string;
  name: string;
  isBot: boolean;
  phoneNumber?: string;
};

type MessageStatus = 'created' | 'sent' | 'delivered' | 'read' | 'failed';

export interface DetectedFunction {
  category: string;
  toolCallId: string;
  functionName: string;
  arguments: Record<string, any>;
}

export interface FileMetadata {
  key: string;
  uuid: string;
  filename: string;
}

export interface Message {
  uuid: string;
  body: string;
  timestamp: string;
  needsEval: boolean;
  status: MessageStatus;
  interlocutor: Interlocutor;
  fileMetadata: FileMetadata | null;
  functions: Array<DetectedFunction>;
  channel?: 'whatsapp' | 'landing' | null;
  extraData?: Record<string, string | number | boolean | RetellCall>;
};

export type MappedMessage = Omit<Message, 'timestamp'> & {
  timestamp: Date;
};

interface ChatUser {
  uuid: string;
  name: string;
  phoneNumber: string;
};

export interface ChatDetail {
  uuid: string;
  test: boolean;
  status: string;
  owner: ChatUser;
  noReply: boolean;
  archived: boolean;
  messages: Array<Message>;
  extraData: Record<string, string | number | boolean>;
};

export type MappedChatDetail = Omit<ChatDetail, 'messages'> & {
  messages: Array<MappedMessage>;
};

interface RetellCallAnalysis {
  call_summary: string;
  user_sentiment: string;
  call_successful: boolean;
}

export interface RetellCall {
  call_id: string;
  agent_id: string;
  call_status: string;
  direction?: string | null;
  call_type?: string | null;
  to_number?: string | null;
  transcript?: string | null;
  from_number?: string | null;
  recording_url?: string | null;
  end_timestamp?: number | null;
  start_timestamp?: number | null;
  call_cost?: Record<string, any> | null;
  call_analysis?: RetellCallAnalysis | null;
}

export const useChatStore = defineStore('chats', () => {
  const evalStore = useEvalStore();
  const aiAgentStore = useAiAgents();

  const chats = ref<Array<MappedChat>>([]);
  const archiveViewMode = ref<'active' | 'archived'>('active');

  // TODO =  only cache N chats to optimize memory usage
  const cachedChats = new Map<string, MappedChatDetail>();

  const loadingSelectedChat = ref(false);
  const selectedChat = ref<MappedChatDetail | null>(null);

  const { $sellebrate } = useNuxtApp();

  const loadingChats = ref(false);
  const loadingArchivedChats = ref(false);
  const totalChats = ref(0);

  const fetchPaginatedChats = async (page: number = 1, pageSize: number = 500) => {
    const response = await $sellebrate<PaginatedChats>('/chats', {
      params: { page, size: pageSize },
    });

    const mappedChats = response.items.map(chat => ({
      ...chat,
      lastMessageDate: chat.lastMessageDate ? new Date(chat.lastMessageDate) : null,
    }));

    chats.value = [...chats.value, ...mappedChats];
    totalChats.value = response.total;

    const hasMorePages = page * pageSize < response.total;

    if (hasMorePages) {
      await fetchPaginatedChats(page + 1, pageSize);
    }
  };

  const fetchChats = async () => {
    if (chats.value.length > 0) return;

    loadingChats.value = true;

    try {
      await fetchPaginatedChats();
    }
    catch (error) {
      throw new Error('Failed to fetch paginated chats', { cause: error });
    }
    finally {
      loadingChats.value = false;
    }
  };

  const sendMessageToChat = async (chatId: string, message: string) => {
    return await $sellebrate<Array<Message> | null>(`/chats/${chatId}`, {
      method: 'post',
      body: { message },
    });
  };

  const clearChat = async (chatId: string, aiAgentStartsConversation: boolean) => {
    await $sellebrate(`/chats/${chatId}?add_first_message=${aiAgentStartsConversation}`, { method: 'delete' });
  };

  const selectChat = async (chatId: string) => {
    if (cachedChats.has(chatId)) {
      selectedChat.value = cachedChats.get(chatId)!;
      return;
    }

    loadingSelectedChat.value = true;
    let chat: ChatDetail | null = null;
    try {
      chat = await $sellebrate<ChatDetail | null>(`/chats/${chatId}`);
    }
    catch (error) {
      console.error('Failed to fetch chat details', error);
    }

    loadingSelectedChat.value = false;
    if (!chat) return;

    selectedChat.value = {
      ...chat,
      messages: chat.messages.map(message => ({
        ...message,
        timestamp: new Date(message.timestamp),
      })),
    };

    cachedChats.set(chatId, selectedChat.value);
  };

  const tryOnWhatsapp = async (
    userName: string,
    phoneNumber: string,
    aiAgentId: string,
    onEvent?: (event: WhatsAppSSEEvent) => void,
  ) => {
    const { public: { baseURL } } = useRuntimeConfig();
    const params = new URLSearchParams({ user_name: userName, ai_agent_id: aiAgentId, phone_number: phoneNumber });
    const url = `${baseURL}/chats/try_on_whatsapp?${params}`;

    try {
      const response = await fetch(url, { method: 'POST', credentials: 'include' });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonData = line.substring(6);
            try {
              const event = JSON.parse(jsonData) as WhatsAppSSEEvent;
              if (onEvent) {
                onEvent(event);
              }
            }
            catch (error) {
              console.error('Failed to parse SSE event:', error);
            }
          }
        }
      }
    }
    catch (error) {
      console.error('Error in tryOnWhatsapp:', error);
      throw error;
    }
  };

  const filterChats = (chats: Ref<MappedChat[]>, status: Ref<string | null>, aiAgent: Ref<string | null>) => {
    const filteredChats = useArrayFilter(chats, (chat) => {
      if (status.value === null && aiAgent.value === null) return true;
      if (status.value === null) return chat.aiAgentName === aiAgent.value;
      if (aiAgent.value === null) return chat.status === status.value;
      return status.value === chat.status && aiAgent.value === chat.aiAgentName;
    });
    return filteredChats;
  };

  const searchChats = (chats: Ref<MappedChat[]>, search: Ref<string>) => {
    const searchedChats = useArrayFilter(chats, (chat) => {
      return chat.owner.name.toLowerCase().includes(search.value.toLowerCase())
        || chat.owner.phoneNumber?.includes(search.value) || false;
    });
    return searchedChats;
  };

  const filterStatus = ref<string | null>(null);
  const filterAIAgent = ref<string | null>(null);

  const search = ref('');

  const displayedChats = computed(() => {
    if (archiveViewMode.value === 'active') {
      return chats.value.filter(c => !c.archived);
    }
    if (archiveViewMode.value === 'archived') {
      return chats.value.filter(c => c.archived);
    }
    return chats.value; // 'all'
  });

  const searchedAndFilteredChats = searchChats(filterChats(displayedChats, filterStatus, filterAIAgent), search);

  const changeStatus = async (chat: MappedChatDetail | null, status: string) => {
    if (!chat) return;

    chat.status = status;
    const chatToUpdate = searchedAndFilteredChats.value.find(c => c.uuid === chat.uuid);
    if (chatToUpdate) {
      chatToUpdate.status = status;
    }
    await $sellebrate(`/chats/${chat.uuid}/status?status=${status}`, { method: 'put' });
    showSnackbar({ autoClose: true, durationMs: 1000, variant: 'success', title: 'Status changed' });
  };

  const closeChat = async (chat: MappedChatDetail, closeStatus: string, assignToHuman: boolean) => {
    await $sellebrate(`/chats/${chat.uuid}/close?close_status=${closeStatus}&assign_to_human=${assignToHuman}`, {
      method: 'put',
    });

    // Update the chat's archived status locally
    const chatToUpdate = chats.value.find(c => c.uuid === chat.uuid);
    if (chatToUpdate) {
      chatToUpdate.archived = true;
    }

    selectedChat.value = null;
    showSnackbar({ autoClose: true, durationMs: 1000, variant: 'success', title: 'Chat closed' });
  };

  const unarchiveChat = async (chat: MappedChatDetail) => {
    await $sellebrate(`/chats/${chat.uuid}/unarchive`, {
      method: 'put',
    });

    // Update the chat's archived status locally
    const chatToUpdate = chats.value.find(c => c.uuid === chat.uuid);
    if (chatToUpdate) {
      chatToUpdate.archived = false;
    }

    // Update selectedChat if it's the current one
    if (selectedChat.value?.uuid === chat.uuid) {
      selectedChat.value.archived = false;
    }

    showSnackbar({ autoClose: true, durationMs: 1000, variant: 'success', title: 'Chat unarchived' });
  };

  const fetchArchivedChats = async () => {
    loadingArchivedChats.value = true;
    try {
      const response = await $sellebrate<PaginatedChats>('/chats', {
        params: { page: 1, size: 500, archived: true },
      });

      const mappedArchivedChats = response.items.map(chat => ({
        ...chat,
        lastMessageDate: chat.lastMessageDate ? new Date(chat.lastMessageDate) : null,
      }));

      // Merge archived chats with existing chats (avoid duplicates)
      mappedArchivedChats.forEach((archivedChat) => {
        if (!chats.value.some(c => c.uuid === archivedChat.uuid)) {
          chats.value.push(archivedChat);
        }
      });
    }
    catch (error) {
      console.error('Failed to fetch archived chats', error);
    }
    finally {
      loadingArchivedChats.value = false;
    }
  };

  const setArchiveViewMode = async (mode: 'active' | 'archived') => {
    archiveViewMode.value = mode;
  };

  const setNoReply = async (chat: MappedChatDetail, noReply: boolean) => {
    await $sellebrate(`/chats/${chat.uuid}/no_reply?value=${noReply}`, { method: 'put' });
    chat.noReply = noReply;
    const chatToUpdate = searchedAndFilteredChats.value.find(c => c.uuid === chat.uuid);
    if (chatToUpdate) {
      chatToUpdate.noReply = noReply;
    }
  };

  const sendTemplate = async (chat: MappedChatDetail, template: string) => {
    await $sellebrate(`/chats/${chat.uuid}/send_template?template_name=${template}`, { method: 'post' });
    selectChat(chat.uuid);
    showSnackbar({ autoClose: true, durationMs: 1000, variant: 'success', title: 'Template sent' });
  };

  const flagMessage = async (messageId: string, feedback: MessageFeedback) => {
    await $sellebrate(`/chats/evaluations/${messageId}/flag_message`, {
      method: 'post',
      body: {
        error_type: feedback.errorType,
        reason: feedback.reason ?? null,
        subcategory: feedback.subcategory ?? null,
        corrected_answer: feedback.correctedAnswer ?? null,
      },
    });

    // Update needsEval in cached and selected chats
    cachedChats.forEach((chat) => {
      const message = chat.messages.find(m => m.uuid === messageId);
      if (message) message.needsEval = true;
    });

    if (selectedChat.value) {
      const message = selectedChat.value.messages.find(m => m.uuid === messageId);
      if (message) message.needsEval = true;
    }

    evalStore.fetchEvalMessages(aiAgentStore.selectedAgent.uuid);
  };

  return {
    chats,
    search,
    clearChat,
    closeChat,
    fetchChats,
    selectChat,
    setNoReply,
    totalChats,
    filterChats,
    flagMessage,
    searchChats,
    changeStatus,
    filterStatus,
    loadingChats,
    selectedChat,
    sendTemplate,
    filterAIAgent,
    tryOnWhatsapp,
    unarchiveChat,
    displayedChats,
    archiveViewMode,
    sendMessageToChat,
    fetchArchivedChats,
    setArchiveViewMode,
    loadingSelectedChat,
    loadingArchivedChats,
    searchedAndFilteredChats,
  };
});
